/**
 * Proposed training plan for one person — read off their latest assessment.
 * Nothing reaches the training board until the lines are approved here.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { DriveIQBadge } from '../../../components/admin/DriveIQ';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Loader2, Sparkles, GraduationCap, CheckCircle2, RefreshCw, AlertCircle, UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr } from '../../../components/admin/billing/shared';

const PRIORITY_TONE = [
  'bg-red-100 text-red-700 border-red-200',
  'bg-amber-100 text-amber-800 border-amber-200',
  'bg-sky-100 text-sky-700 border-sky-200',
];

export const AiTrainingPlanCard = ({ staffId, staffName, canEdit }) => {
  const { authHeader } = useAuth();
  const [plan, setPlan] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/skills/staff/${staffId}/training-plan`,
        { headers: authHeader() });
      if (data?.items?.length) {
        setPlan(data);
        setPicked(data.items.map((i) => i.skill_id).filter(Boolean));
      }
    } catch { /* no proposal yet — the empty state explains itself */ }
  }, [authHeader, staffId]);

  useEffect(() => { load(); }, [load]);

  const propose = async () => {
    setBusy('gen');
    try {
      const { data } = await axios.post(
        `${API}/skills/staff/${staffId}/training-plan/propose`, {},
        { headers: authHeader(), timeout: 180000 });
      setPlan(data);
      setPicked((data.items || []).map((i) => i.skill_id).filter(Boolean));
      toast.success(data.items?.length
        ? `${data.items.length} step(s) proposed for ${staffName}`
        : 'No gaps to train right now');
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const approve = async () => {
    setBusy('approve');
    try {
      const { data } = await axios.post(
        `${API}/skills/staff/${staffId}/training-plan/approve`,
        { skill_ids: picked }, { headers: authHeader() });
      toast.success(`${data.created} task(s) added to the training plan`
        + (data.updated_existing ? ` · ${data.updated_existing} existing task(s) updated` : ''));
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="ai-training-plan-card">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Sparkles size={15} className="text-teal-600" /> Proposed training plan
            <DriveIQBadge busy={busy === 'gen'} testId="training-plan-driveiq" />
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Built from this person&apos;s latest assessment — what to train first, who trains
            them and how long it should take.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={propose} disabled={!!busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700 disabled:opacity-50"
            data-testid="propose-plan-btn"
          >
            {busy === 'gen'
              ? <><Loader2 size={15} className="animate-spin" /> Reading the assessment…</>
              : <>{plan ? <RefreshCw size={15} /> : <Sparkles size={15} />} {plan ? 'Redo plan' : 'Propose plan'}</>}
          </button>
        )}
      </div>

      {!plan && (
        <p className="text-sm text-slate-500" data-testid="no-plan-yet">
          No plan proposed yet.{canEdit ? ' Use “Propose plan” to draft one from the recorded gaps.' : ''}
        </p>
      )}

      {plan && (
        <div className="space-y-3">
          {plan.summary && (
            <p className="text-sm text-slate-700 bg-teal-50/60 border border-teal-100 rounded-lg p-3"
               data-testid="plan-summary">
              {plan.summary}
            </p>
          )}
          {plan.watch_out && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex gap-1.5"
               data-testid="plan-watchout">
              <AlertCircle size={14} className="mt-0.5 shrink-0" /> {plan.watch_out}
            </p>
          )}

          <div className="space-y-2">
            {plan.items.map((i, n) => (
              <label
                key={i.skill_id || i.skill}
                className="flex gap-3 rounded-lg border border-slate-200 p-3 hover:bg-slate-50 cursor-pointer"
                data-testid={`plan-item-${n}`}
              >
                <input
                  type="checkbox" className="mt-1 h-4 w-4 accent-teal-600"
                  checked={picked.includes(i.skill_id)}
                  onChange={() => toggle(i.skill_id)}
                  disabled={!canEdit || !i.skill_id}
                  data-testid={`plan-item-check-${n}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full border ${PRIORITY_TONE[Math.min(n, 2)]}`}>
                      {n + 1}
                    </span>
                    <span className="text-sm font-medium text-slate-900">{i.skill}</span>
                    <span className="text-xs text-slate-400">{i.skill_group}</span>
                    <span className="text-xs text-slate-500">· now: {i.current_level}</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">{i.why}</p>
                  <p className="text-sm text-slate-700 mt-1">{i.method}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <UserCheck size={12} />
                      {i.trainer_name || 'no expert on the floor — owner/manager to train'}
                    </span>
                    <span>{i.days} working days · by {i.due_date}</span>
                    <span className="text-slate-400">target: {i.target_level === 'expert' ? 'can train others' : 'OK'}</span>
                  </div>
                </div>
              </label>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <span className="text-xs text-slate-400" data-testid="plan-generated-at">
              Drafted {String(plan.generated_at || '').replace('T', ' ').slice(0, 16)}
              {plan.approved_at ? ` · approved ${String(plan.approved_at).replace('T', ' ').slice(0, 16)}` : ''}
            </span>
            <div className="flex items-center gap-2">
              <Link to="/admin/skills/training"
                    className="text-xs text-teal-700 hover:text-teal-900 font-medium"
                    data-testid="plan-board-link">
                Training board →
              </Link>
              {canEdit && (
                <button
                  onClick={approve} disabled={!!busy || !picked.length}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50"
                  data-testid="approve-plan-btn"
                >
                  {busy === 'approve'
                    ? <Loader2 size={15} className="animate-spin" />
                    : <CheckCircle2 size={15} />}
                  Approve {picked.length} step(s)
                </button>
              )}
            </div>
          </div>
          <p className="text-[11px] text-slate-400 flex items-center gap-1">
            <GraduationCap size={12} /> Approving creates dated tasks on the training board and
            updates any task already open on the same skill — the staff member then sees the plan,
            in their own language, on My Performance.
          </p>
        </div>
      )}
    </div>
  );
};

export default AiTrainingPlanCard;
